// Deployment pipeline step model.
//
// Ported from the legacy src/components/DeploymentProgressBar.tsx so the v2
// progress visualization (DeploymentProgress.tsx) and its details dialog share
// one source of truth. The matching is intentionally fuzzy (substring on the
// backend-provided `stage`/`step` text), with two guards against the fuzziness
// landing on the wrong step — see getCurrentStepIndex.

export interface StepDefinition {
  name: string;
  shortName?: string;
  /** Other `step` values the backend writes while this step is current. */
  aliases?: string[];
}

// Predefined step sequences for each deployment type.
export const STEP_DEFINITIONS: Record<string, StepDefinition[]> = {
  lambda: [
    { name: 'PreDeployment', shortName: 'Pre' },
    { name: 'Starting Lambda Deployment', shortName: 'Start' },
    { name: 'Updating Lambda Function', shortName: 'Update' },
    { name: 'PostDeployment', shortName: 'Post' },
  ],
  ecs: [
    { name: 'PreDeployment', shortName: 'Pre' },
    { name: 'Preparing ECS Deployment', shortName: 'Prepare' },
    { name: 'Updating ECS Service', shortName: 'Update' },
    { name: 'Monitoring Deployment', shortName: 'Monitor' },
    { name: 'PostDeployment', shortName: 'Post' },
  ],
  terraform: [
    { name: 'PreDeployment', shortName: 'Pre' },
    { name: 'Running Terraform Plan', shortName: 'Plan' },
    {
      name: 'Terraform Plan Completed',
      shortName: 'Review',
      // The Argo deployer's review gate (cd-deployer internal/deploy/terraform/approval.go).
      aliases: ['Awaiting Plan Review', 'Awaiting Approval Decision'],
    },
    { name: 'Terraform Approved - Starting Apply', shortName: 'Apply' },
    { name: 'PostDeployment', shortName: 'Post' },
  ],
  static: [
    { name: 'PreDeployment', shortName: 'Pre' },
    { name: 'Copying Static Assets', shortName: 'Copy' },
    { name: 'PostDeployment', shortName: 'Post' },
  ],
  adhoc: [
    { name: 'PreDeployment', shortName: 'Pre' },
    { name: 'Running Adhoc Task', shortName: 'Run' },
    { name: 'PostDeployment', shortName: 'Post' },
  ],
  'agentcore-runtime': [
    { name: 'PreDeployment', shortName: 'Pre' },
    { name: 'Starting AgentCore Deployment', shortName: 'Start' },
    { name: 'Monitoring Runtime Update', shortName: 'Monitor' },
    { name: 'PostDeployment', shortName: 'Post' },
  ],
};

/** Step sequence for a deployment type; unknown / missing types fall back to lambda. */
export const getStepsForType = (type?: string): StepDefinition[] =>
  STEP_DEFINITIONS[(type || '').toLowerCase()] ?? STEP_DEFINITIONS.lambda;

/**
 * Index of the "current" step within `steps`, given the deployment status and
 * the backend's free-text `stage` / `step`.
 *
 *  - returns `-1` when there is no stage/step to anchor on (nothing to draw)
 *  - returns `steps.length` for a successful deploy (every step complete)
 *  - for a failure, the index of the step it failed on (last step if unknown)
 *  - otherwise the matched in-progress step, falling back to a stage estimate
 */
export const getCurrentStepIndex = (
  steps: StepDefinition[],
  status?: string,
  stage?: string,
  step?: string
): number => {
  if (!step && !stage) return -1;

  const s = status?.toLowerCase();

  // Terminal: success → all steps complete.
  if (s === 'success') return steps.length;

  // Terminal: failed → find where it failed.
  if (s === 'failed') {
    const failedIndex = steps.findIndex(d => step?.toLowerCase().includes(d.name.toLowerCase()));
    return failedIndex >= 0 ? failedIndex : steps.length - 1;
  }

  // In progress, still in the PreDeployment stage → the first step, whatever
  // the step text says. Every record is born `PreDeployment` / `Starting`, and
  // "Starting" is a substring of later step names ("Terraform Approved -
  // Starting Apply"), which used to put a brand-new terraform job on Apply.
  if (stage?.toLowerCase() === 'predeployment') return 0;

  // In progress: match the current step name. The step text containing a
  // definition name (or alias) is the reliable direction; the reverse — a
  // definition name containing the step text — is kept for legacy partial step
  // values, but only for multi-word ones, so a lone generic word like
  // "Starting" or "Deployment" can't land on whichever step happens to contain it.
  const lowerStep = step?.toLowerCase() || '';
  const names = (d: StepDefinition) => [d.name, ...(d.aliases ?? [])].map(n => n.toLowerCase());
  let stepIndex = lowerStep ? steps.findIndex(d => names(d).some(n => lowerStep.includes(n))) : -1;
  if (stepIndex === -1 && lowerStep.includes(' ')) {
    stepIndex = steps.findIndex(d => names(d).some(n => n.includes(lowerStep)));
  }

  // No step match → estimate position from the stage.
  if (stepIndex === -1 && stage) {
    if (stage.toLowerCase().includes('pre')) return 0;
    if (stage.toLowerCase().includes('post')) return steps.length - 1;
    return Math.floor(steps.length / 2);
  }

  return stepIndex;
};
